import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, TrendingUp, MapPin, BarChart3, ArrowUpRight, Info, Download, Building2, Target } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import DataFreshnessStrip from '../components/DataFreshnessStrip';
import { api } from '../api';

const Building2Icon = () => <Building2 size={13} />;

function fmtPct(v, d = 1) {
  if (v == null || isNaN(v)) return '—';
  return `${Number(v).toFixed(d)}%`;
}
function fmtInt(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString();
}

function Stat({ value, label, accent, sub }) {
  return (
    <div className="p-4 rounded-xl bg-white border border-[#E5DCC4]">
      <div className={`font-display text-2xl tracking-tight ${accent || 'text-[#1A2622]'}`}>{value}</div>
      <div className="mt-1 text-[10.5px] uppercase tracking-wider text-[#7A857F]">{label}</div>
      {sub && <div className="mt-1 text-[11px] text-[#5A6660]">{sub}</div>}
    </div>
  );
}

function StateRow({ row, rankAccessor, valueAccessor, valueFmt }) {
  return (
    <Link to={`/managed-care/state/${row.state_code}`}
          className="flex items-center justify-between px-4 py-3 border-t border-[#F0E7D2] first:border-t-0 hover:bg-[#FBF6EC]" data-testid={`ma-state-row-${row.state_code}`}>
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-[11px] text-[#7A857F] w-6">{row[rankAccessor] || '—'}</span>
        <span className="text-[13px] font-medium text-[#1A2622] truncate">{row.state_name}</span>
        <span className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">{row.state_code}</span>
      </div>
      <div className="flex items-center gap-3">
        {row.opportunity && (
          <span className="hidden md:inline-flex px-2 py-0.5 rounded-md text-[10px] font-semibold"
                style={{ background: `${row.opportunity.color}1A`, color: row.opportunity.color }}>
            {row.opportunity.label}
          </span>
        )}
        <span className="text-[13px] font-semibold text-[#1A2622] tabular-nums">
          {valueFmt(row[valueAccessor])}
        </span>
        <ArrowUpRight size={13} className="text-[#A8B0AA]" />
      </div>
    </Link>
  );
}

export default function ManagedCarePage() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [targets, setTargets] = useState({ items: [], total: 0 });

  useEffect(() => {
    setLoading(true);
    api.maDashboard().then(setD).finally(() => setLoading(false));
    api.mcTargets({ min_beds: 40, min_skilled: 15, min_county_ma: 25, min_occupancy: 70, limit: 8 })
      .then(setTargets).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-12">
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Managed Care Intelligence' }]} />
        <div className="mt-3"><DataFreshnessStrip /></div>

        <header className="mt-6 max-w-3xl">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#4A7C5E]/30 bg-white">
            <Activity size={12} className="text-[#4A7C5E]" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#4A7C5E]">Managed Care Intelligence · CMS data</span>
          </div>
          <h1 className="mt-3 font-display text-4xl md:text-5xl font-semibold text-[#1A2622] tracking-tight leading-tight">
            Where Medicare Advantage is winning — and where it isn't
          </h1>
          <p className="mt-3 text-[14px] text-[#3A4A44] leading-relaxed">
            County-level Medicare Advantage penetration overlaid on every SNF in our index.
            See which markets are saturated, which still skew fee-for-service, and how MA
            beneficiary volume, dual-eligible mix, and SNF utilization compare across states.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Link to="/mc-targets" className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-[#34538F] text-white text-[12.5px] hover:bg-[#1A2622]" data-testid="mc-targets-link">
              <Target size={13} /> Contract Targets
            </Link>
            <Link to="/managed-care/payers" className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-[#4A7C5E] text-white text-[12.5px] hover:bg-[#2D5F4F]" data-testid="payers-link">
              <Building2Icon /> View MA payers
            </Link>
            <a href={api.exportManagedCareCSV()} download className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-[#1A2622] text-[#FBF6EC] text-[12.5px] hover:bg-[#2D5F4F]" data-testid="export-ma-csv-btn">
              <Download size={13}/> Export state CSV
            </a>
            <a href={api.exportFacilitiesCSV({ ma_opportunity: 'high', limit: 5000 })} download className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-white border border-[#E5DCC4] text-[12.5px] text-[#3A4A44] hover:border-[#4A7C5E]" data-testid="export-fac-high-opp-btn">
              <Download size={13}/> Export high-opportunity facilities
            </a>
          </div>
        </header>

        {loading && <div className="mt-8 text-sm text-[#7A857F]">Loading managed care intelligence…</div>}
        {!loading && d && !d.ready && (
          <div className="mt-8 rounded-xl border border-dashed border-[#E5DCC4] p-8 bg-white">
            <div className="text-[13px] text-[#7A857F]">Managed care data is being prepared. Please refresh in a moment.</div>
          </div>
        )}

        {!loading && d?.ready && (
          <>
            <section className="mt-7 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ma-dashboard-stats">
              <Stat value={fmtPct(d.national_avg_penetration, 1)} label="National avg MA penetration" accent="text-[#4A7C5E]" />
              <Stat value={fmtInt(d.states_indexed)} label="States + DC indexed" />
              <Stat value={fmtInt(d.counties_indexed)} label="Counties analyzed" />
              <Stat value={d.highest_penetration?.[0]?.state_code || '—'} label="Most penetrated state"
                    sub={d.highest_penetration?.[0] ? `${d.highest_penetration[0].state_name} · ${fmtPct(d.highest_penetration[0].latest_penetration, 1)}` : ''} />
            </section>

            <section className="mt-7 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
                <div className="px-5 py-4 border-b border-[#F0E7D2] flex items-center gap-2">
                  <BarChart3 size={14} className="text-[#C75D5D]"/>
                  <h2 className="font-display text-base font-semibold text-[#1A2622]">Most penetrated MA markets</h2>
                  <span className="ml-auto text-[10.5px] uppercase tracking-wider text-[#7A857F]">Highest saturation</span>
                </div>
                {d.highest_penetration.map((r, i) => (
                  <StateRow key={r.state_code} row={{ ...r, rank: i + 1 }} rankAccessor="rank" valueAccessor="latest_penetration" valueFmt={(v) => fmtPct(v, 1)} />
                ))}
              </div>
              <div className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
                <div className="px-5 py-4 border-b border-[#F0E7D2] flex items-center gap-2">
                  <MapPin size={14} className="text-[#4A7C5E]"/>
                  <h2 className="font-display text-base font-semibold text-[#1A2622]">Highest contracting opportunity</h2>
                  <span className="ml-auto text-[10.5px] uppercase tracking-wider text-[#7A857F]">Lowest penetration</span>
                </div>
                {d.highest_opportunity.map((r, i) => (
                  <StateRow key={r.state_code} row={{ ...r, rank: i + 1 }} rankAccessor="rank" valueAccessor="latest_penetration" valueFmt={(v) => fmtPct(v, 1)} />
                ))}
              </div>
            </section>

            {(d.fastest_growth || []).length > 0 && (
              <section className="mt-4 rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
                <div className="px-5 py-4 border-b border-[#F0E7D2] flex items-center gap-2">
                  <TrendingUp size={14} className="text-[#4A7C5E]"/>
                  <h2 className="font-display text-base font-semibold text-[#1A2622]">Fastest MA beneficiary growth (2016–2022)</h2>
                </div>
                {d.fastest_growth.map((r, i) => (
                  <StateRow key={r.state_code} row={{ ...r, rank: i + 1 }} rankAccessor="rank" valueAccessor="trend_pct_change" valueFmt={(v) => v != null ? `${v > 0 ? '+' : ''}${v}%` : '—'} />
                ))}
              </section>
            )}

            <section className="mt-4 rounded-xl bg-white border border-[#E5DCC4] overflow-hidden" data-testid="mc-targets-section">
              <div className="px-5 py-4 border-b border-[#F0E7D2] flex items-center gap-2 flex-wrap">
                <Target size={14} className="text-[#34538F]" />
                <h2 className="font-display text-base font-semibold text-[#1A2622]">Top MC Contract Targets</h2>
                <span className="text-[10.5px] text-[#7A857F]">Best-fit prospects — high skilled mix &amp; high county MA gap</span>
                <Link to="/mc-targets" className="ml-auto inline-flex items-center gap-1 text-[12px] text-[#34538F] hover:underline" data-testid="mc-targets-viewall">
                  View all {fmtInt(targets.total)} <ArrowUpRight size={12} />
                </Link>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[12.5px]">
                  <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
                    <tr>
                      <th className="text-left px-4 py-2.5">Facility</th>
                      <th className="text-right px-4 py-2.5">Beds</th>
                      <th className="text-right px-4 py-2.5">Skilled</th>
                      <th className="text-right px-4 py-2.5">MC now</th>
                      <th className="text-right px-4 py-2.5">County MA</th>
                      <th className="text-right px-4 py-2.5">Gap</th>
                      <th className="text-right px-4 py-2.5">Best-fit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(targets.items || []).length === 0 && (
                      <tr><td colSpan={7} className="px-4 py-6 text-center text-[#7A857F]">No prospects matched the default filters. Open Contract Targets to loosen criteria.</td></tr>
                    )}
                    {(targets.items || []).map((r) => (
                      <tr key={r.ccn} className="border-t border-[#F0E7D2] hover:bg-[#FBF6EC]" data-testid={`mc-target-row-${r.ccn}`}>
                        <td className="px-4 py-2.5">
                          <Link to={`/facilities/${r.ccn}`} className="text-[#1A2622] font-medium hover:text-[#34538F]">{r.name}</Link>
                          <div className="text-[10.5px] text-[#7A857F]">{r.city ? `${r.city}, ` : ''}{r.state} · CCN {r.ccn}</div>
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{fmtInt(r.beds)}</td>
                        <td className="px-4 py-2.5 text-right text-[#34538F] tabular-nums font-semibold">{fmtPct(r.pct_skilled_est)}</td>
                        <td className="px-4 py-2.5 text-right text-[#A06628] tabular-nums">{fmtPct(r.pct_managed_care_est)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{fmtPct(r.county_ma_pct)}</td>
                        <td className="px-4 py-2.5 text-right">
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] font-semibold bg-[#E0EAF8] text-[#34538F]">+{r.mc_gap?.toFixed(1)} pts</span>
                        </td>
                        <td className="px-4 py-2.5 text-right font-semibold text-[#34538F] tabular-nums">{r.best_fit_score?.toFixed(0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
                <div className="px-5 py-4 border-b border-[#F0E7D2] flex items-center gap-2">
                  <h2 className="font-display text-base font-semibold text-[#1A2622]">Counties with the highest MA penetration</h2>
                </div>
                <ul>
                  {(d.top_counties_penetration || []).map((c, i) => (
                    <li key={`hp-${c.fips || i}`} className="flex items-center justify-between px-4 py-3 border-t border-[#F0E7D2] first:border-t-0">
                      <div className="min-w-0">
                        <div className="text-[13px] font-medium text-[#1A2622] truncate">{c.county_name}</div>
                        <div className="text-[10.5px] text-[#7A857F]">{c.state_name} · {fmtInt(c.enrolled)} of {fmtInt(c.eligibles)} enrolled</div>
                      </div>
                      <span className="text-[13px] font-semibold text-[#C75D5D] tabular-nums">{fmtPct(c.penetration_pct, 1)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
                <div className="px-5 py-4 border-b border-[#F0E7D2] flex items-center gap-2">
                  <h2 className="font-display text-base font-semibold text-[#1A2622]">Counties with the lowest MA penetration</h2>
                  <span className="ml-auto text-[10.5px] uppercase tracking-wider text-[#7A857F]">≥ 1k eligibles</span>
                </div>
                <ul>
                  {(d.top_counties_opportunity || []).map((c, i) => (
                    <li key={`op-${c.fips || i}`} className="flex items-center justify-between px-4 py-3 border-t border-[#F0E7D2] first:border-t-0">
                      <div className="min-w-0">
                        <div className="text-[13px] font-medium text-[#1A2622] truncate">{c.county_name}</div>
                        <div className="text-[10.5px] text-[#7A857F]">{c.state_name} · {fmtInt(c.eligibles)} eligibles</div>
                      </div>
                      <span className="text-[13px] font-semibold text-[#4A7C5E] tabular-nums">{fmtPct(c.penetration_pct, 1)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            <div className="mt-6 flex items-start gap-2 text-[11.5px] text-[#A8B0AA]">
              <Info size={12} className="mt-0.5 shrink-0" />
              <span>
                Source: {d.source}. Secondary trend metrics from {d.secondary_source}. Opportunity scores
                are computed from county/state penetration vs. the national average — lower penetration
                indicates higher headroom for managed-care contracting. Data is additive to existing CMS
                modules and does not modify provider, ownership, financial, penalty or survey records.
              </span>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
